import { EditOutlined, PlusOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Card, Empty, message, Skeleton, Tooltip, Typography } from 'antd';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { queryErrorMessage } from '../CashTransactions/types';
import AppIcon from './AppIcon';
import MobileAppFormDrawer from './MobileAppFormDrawer';
import type { MobileAppCard } from './types';

const { Title, Text } = Typography;

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';

// Every Mobile app as a card with its name and description; opening one lists
// its versions.
export default function MobileAppList() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';
  const navigate = useNavigate();
  const { appKey } = useParams<{ appKey: string }>();
  const [apps, setApps] = useState<MobileAppCard[] | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    runQuery<MobileAppCard>('mobile_apps', {})
      .then(setApps)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery]);

  useEffect(() => {
    load();
  }, [load]);

  // The drawer's app: null = a new one; closed when formOpen is false.
  const [formOpen, setFormOpen] = useState(false);
  const [formApp, setFormApp] = useState<MobileAppCard | null>(null);
  const create = () => {
    setFormApp(null);
    setFormOpen(true);
  };
  const edit = (app: MobileAppCard) => {
    setFormApp(app);
    setFormOpen(true);
  };
  const open = (id: number) => navigate(`/apps/${appKey}/mobile_apps/versions/${id}`);

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Мобайл апп
            </Title>
            <Text type="secondary">
              Сургуулийн мобайл аппууд ба тэдгээрийн хувилбарууд
              {apps && apps.length > 0 && ` · ${apps.length} апп`}
            </Text>
          </div>
          <div className="flex gap-2">
            <Tooltip title="Шинэчлэх">
              <Button icon={<ReloadOutlined />} loading={loading && apps !== null} onClick={load} />
            </Tooltip>
          </div>
        </div>

        {apps === null ? (
          <div className={GRID}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-2xl border border-gray-200 p-5">
                <Skeleton avatar={{ shape: 'square', size: 56 }} active paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
        ) : apps.length === 0 && !canEdit ? (
          <Empty description="Апп бүртгэгдээгүй байна" className="py-12" />
        ) : (
          <div className={GRID}>
            {apps.map((app) => (
              <AppCard
                key={app.id}
                app={app}
                canEdit={canEdit}
                onOpen={() => open(app.id)}
                onEdit={() => edit(app)}
              />
            ))}
            {canEdit && (
              <button
                type="button"
                onClick={create}
                className="flex min-h-[200px] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 bg-transparent text-gray-400 transition-colors hover:border-blue-400 hover:text-blue-500"
              >
                <PlusOutlined className="text-2xl" />
                <span className="text-sm font-medium">Апп нэмэх</span>
              </button>
            )}
          </div>
        )}
      </Card>

      <MobileAppFormDrawer
        open={formOpen}
        app={formApp}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          load();
        }}
      />
    </LayoutWithMenu>
  );
}

interface AppCardProps {
  app: MobileAppCard;
  canEdit: boolean;
  onOpen: () => void;
  onEdit: () => void;
}

function AppCard({ app, canEdit, onOpen, onEdit }: AppCardProps) {
  return (
    <div className="group relative flex min-h-[200px] flex-col rounded-2xl border border-gray-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-lg has-[button:focus-visible]:border-blue-400">
      {/* The whole card opens the app; the edit button sits above it. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${app.name} — хувилбарууд`}
        className="absolute inset-0 cursor-pointer rounded-2xl border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
      />
      {canEdit && (
        <Tooltip title="Засах">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            aria-label="Засах"
            className="absolute! top-3 right-3 z-10 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          />
        </Tooltip>
      )}

      <div className="pointer-events-none flex items-center gap-4 pr-6">
        <div className="shrink-0 rounded-2xl shadow-sm ring-1 ring-black/5">
          <AppIcon url={app.icon_url} size={56} />
        </div>
        <div className="min-w-0">
          <div className="line-clamp-2 text-base leading-snug font-semibold text-gray-800">
            {app.name}
          </div>
          <span className="mt-1 inline-block rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-500">
            {app.code}
          </span>
        </div>
      </div>

      <p
        className="pointer-events-none mt-4 mb-0 line-clamp-3 text-sm leading-relaxed text-gray-500"
        title={app.description ?? undefined}
      >
        {app.description || <span className="italic text-gray-300">Тайлбаргүй</span>}
      </p>

      <div className="pointer-events-none mt-auto flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
        {app.latest_version ? (
          <div className="flex min-w-0 items-center gap-2">
            <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-600">
              v{app.latest_version}
            </span>
            {app.latest_uploaded_at && (
              <span className="truncate text-xs text-gray-400">
                {dayjs(app.latest_uploaded_at).format('YYYY.MM.DD')}
              </span>
            )}
          </div>
        ) : (
          <span className="flex items-center gap-1.5 text-xs text-gray-400">
            <span className="size-1.5 rounded-full bg-gray-300" />
            Хувилбар нэмэгдээгүй
          </span>
        )}
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-blue-600">
          {app.version_count > 0 ? `${app.version_count} хувилбар` : 'Дэлгэрэнгүй'}
          <RightOutlined className="text-[10px] transition-transform group-hover:translate-x-0.5" />
        </span>
      </div>
    </div>
  );
}
